
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import getQrSigningUrl from "../../components/SigningUrls";

import API_URL from "../../api/API";
import { useBusinessAuthContext } from "./useBusinessAuthContext";
import Sidebar from "../../components/Sidebar";

const getHeaders = (business) => ({
  Authorization: `Bearer ${business.token}`,
  "x-session-id": business.sessionId,
  "x-frontend-url": window.location.pathname,
});

const getSlug = (...values) =>
  values.find(
    (value) => typeof value === "string" && value.trim()
  )?.trim() || "";

const getService = (waiver) => {
  const services = waiver.businessTypeId?.services || [];

  return services.find(
    (service) =>
      String(service._id) === String(waiver.serviceId?._id || waiver.serviceId)
  );
};

const getSigningUrl = (location, waiver) => {
  const businessType = waiver.businessTypeId;
  const service = getService(waiver);

  const locationSlug = getSlug(
    location.uniqueSlug,
    location.slug
  );

  const businessTypeSlug = getSlug(
    businessType?.uniqueSlug,
    businessType?.slug
  );

  if (!locationSlug || !businessTypeSlug) {
    return "";
  }

  const baseUrl =
    `${window.location.origin}/start-waiver/` +
    `${encodeURIComponent(locationSlug)}/` +
    `${encodeURIComponent(businessTypeSlug)}`;

  const serviceSlug = getSlug(
    service?.uniqueSlug,
    service?.slug
  );

  return serviceSlug
    ? `${baseUrl}/${encodeURIComponent(serviceSlug)}`
    : baseUrl;
};

const getLocationSigningUrl = (location, waiver) => {
  const locationSlug = getSlug(
    location?.uniqueSlug,
    location?.slug
  );

  const businessTypeSlug = getSlug(
    waiver?.businessTypeId?.uniqueSlug,
    waiver?.businessTypeId?.slug
  );

  if (!locationSlug || !businessTypeSlug) {
    return "";
  }

  return (
    `${window.location.origin}/start-waiver/` +
    `${encodeURIComponent(locationSlug)}/` +
    `${encodeURIComponent(businessTypeSlug)}`
  );
};

const getWaiverStatus = (waiver) =>
  waiver.waiverStatus === "published" ? "Active" : "Disabled";

const QRCodeActions = ({ url, copyUrl = url, label, id }) => {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
      try {
          await navigator.clipboard.writeText(copyUrl);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 2000);
      } catch (error) {
          console.error("Failed to copy signing link:", error);
          window.prompt("Copy this signing link:", copyUrl);
      }
  };

  const downloadQR = () => {
    const svg = document.getElementById(id);

    if (!svg) return;

    const svgClone = svg.cloneNode(true);
    svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg");

    const source = new XMLSerializer().serializeToString(svgClone);
    const blob = new Blob([source], {
      type: "image/svg+xml;charset=utf-8",
    });

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = `${label.replace(/[^a-z0-9-_]/gi, "-")}-qr.svg`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(objectUrl);
  };

  const printQR = () => {
    const svg = document.getElementById(id);

    if (!svg) return;

    const svgMarkup = new XMLSerializer().serializeToString(
      svg.cloneNode(true)
    );

    const printWindow = window.open("", "_blank", "width=600,height=700");

    if (!printWindow) {
      window.alert("Please allow pop-ups to print the QR code.");
      return;
    }

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${label}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 32px;
            }
            svg {
              width: 280px;
              height: 280px;
              max-width: 100%;
            }
            p {
              overflow-wrap: anywhere;
              font-size: 12px;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <h2>${label}</h2>
          ${svgMarkup}
          <p>${url}</p>
          <script>
            window.onload = () => {
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
      <button
        type="button"
        onClick={downloadQR}
        className="inline-flex items-center gap-1.5 text-gray-600 hover:text-black"
      >
        <span aria-hidden="true">↓</span>
        Download
      </button>

      <button
        type="button"
        onClick={printQR}
        className="inline-flex items-center gap-1.5 text-gray-600 hover:text-black"
      >
        <span aria-hidden="true">▣</span>
        Print
      </button>

      <button
        type="button"
        onClick={copyLink}
        className="inline-flex items-center gap-1.5 text-gray-600 hover:text-black"
      >
        <span aria-hidden="true">⧉</span>
        {copied ? "Copied!" : "Copy Link"}
      </button>
    </div>
  );
};

const QRCodes = () => {
  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [waivers, setWaivers] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!business) {
      navigate("/business/login");
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const headers = getHeaders(business);

        const [locationResponse, waiverResponse] = await Promise.all([
          fetch(API_URL.BUSINESS_LOCATIONS, { headers }),
          fetch(API_URL.BUSINESS_WAIVERS_LIST, { headers }),
        ]);

        const [locationData, waiverData] = await Promise.all([
          locationResponse.json(),
          waiverResponse.json(),
        ]);

        if (!locationResponse.ok || !locationData.success) {
          throw new Error(
            locationData.message || "Failed to load locations."
          );
        }

        if (!waiverResponse.ok || !waiverData.success) {
          throw new Error(
            waiverData.message || "Failed to load waivers."
          );
        }

        if (cancelled) return;

        const activeLocations = (locationData.locations || []).filter(
          (location) =>
            !location.isDeleted && location.isActive === true
        );

        /*const availableWaivers = (waiverData.waivers || []).filter(
          (waiver) =>
            !waiver.isDeleted &&
            waiver.waiverStatus !== "new_version" &&
            [
              "published",
              "draft",
              "archived_by_admin",
              "archived_by_new_version",
              "archived_by_location_deletion",
            ].includes(waiver.waiverStatus)
        );*/
        const availableWaivers = (waiverData.waivers || []).filter(
        (waiver) =>
            !waiver.isDeleted &&
            waiver.waiverStatus === "published"
        );
        
        setLocations(activeLocations);
        setWaivers(availableWaivers);

        setSelectedLocationId((current) => {
          if (
            current &&
            activeLocations.some(
              (location) => String(location._id) === current
            )
          ) {
            return current;
          }

          return String(activeLocations[0]?._id || "");
        });
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load QR codes:", err);
          setError(err.message || "Failed to load QR codes.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [business, navigate]);

  const selectedLocation = locations.find(
    (location) => String(location._id) === selectedLocationId
  );

  const locationWaivers = useMemo(() => {
    if (!selectedLocation) return [];

    return waivers
      .filter((waiver) =>
        (waiver.locations || []).some(
          (location) =>
            String(location._id || location) ===
            String(selectedLocation._id)
        )
      )
      .sort((a, b) => {
        const serviceA = getService(a)?.name || "";
        const serviceB = getService(b)?.name || "";

        return (
          serviceA.localeCompare(serviceB) ||
          new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
      });
  }, [waivers, selectedLocation]);

  
    const locationSigningUrl = selectedLocation
    ? getLocationSigningUrl(
        selectedLocation,
        locationWaivers.find((waiver) =>
            getLocationSigningUrl(selectedLocation, waiver)
        )
        )
    : "";
    const locationQrUrl = getQrSigningUrl(locationSigningUrl);


 
  if (loading) {
    return (
      <div className="h-screen flex flex-row bg-background">
        <Sidebar />
        <div className="flex-1 overflow-auto py-9">
          <div className="max-w-6xl mx-auto px-6">
            Loading QR codes...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar />

      <main className="flex-1 min-w-0 overflow-auto py-8 md:py-9">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <h1 className="text-2xl font-medium text-darkLighted">
              QR Codes
            </h1>
            <p className="text-sm text-foreground mt-1">
              Always links to the latest active versions
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {locations.length === 0 ? (
            <div className="rounded-2xl border border-stroked bg-white p-8 text-center">
              <h2 className="font-semibold text-darkLighted">
                No active locations
              </h2>
              <p className="mt-2 text-sm text-foreground">
                Create or activate a business location to generate QR codes.
              </p>
              <button
                type="button"
                onClick={() => navigate("/business/locations")}
                className="mt-5 rounded-full bg-highlited px-5 py-2.5 font-medium hover:bg-[#D5AA00]"
              >
                Manage Locations
              </button>
            </div>
          ) : (
            <>
              <div className="mb-5 rounded-2xl border border-stroked bg-white p-4 md:p-5">
                <label
                  htmlFor="qr-location"
                  className="mb-2 block text-sm font-medium text-darkLighted"
                >
                  Select Location
                </label>

                <select
                  id="qr-location"
                  value={selectedLocationId}
                  onChange={(event) =>
                    setSelectedLocationId(event.target.value)
                  }
                  className="w-full max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-gray-600"
                >
                  {locations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.name}
                      {location.city ? ` — ${location.city}` : ""}
                    </option>
                  ))}
                </select>

                {selectedLocation && (
                  <p className="mt-2 text-xs text-gray-500">
                    {[
                      selectedLocation.street,
                      selectedLocation.city,
                      selectedLocation.state,
                      selectedLocation.zip,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
              </div>

              <section className="mb-5 rounded-2xl border border-stroked bg-white p-5 md:p-6">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                  <div className="flex h-32 w-32 shrink-0 items-center justify-center rounded-2xl border-2 border-gray-900 bg-white">
                    {locationQrUrl ? (
                      <QRCodeSVG
                        id="location-qr-code"
                        value={locationQrUrl}
                        size={112}
                        includeMargin
                      />
                    ) : (
                      <span className="px-3 text-center text-xs text-gray-500">
                        No location signing URL available
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-semibold text-darkLighted">
                      Location QR Code
                    </h2>
                    <p className="mt-1 text-sm text-foreground">
                      {selectedLocation?.name} — customers scan to open
                      the signing flow for this location.
                    </p>

                    <div className="mt-4">
                      {locationQrUrl ? (
                        <QRCodeActions
                          id="location-qr-code"
                          url={locationQrUrl}
                          copyUrl={locationSigningUrl}
                          label={`${selectedLocation?.name || "Location"}-signing`}
                        />
                      ) : (
                        <p className="text-sm text-amber-700">
                          Assign a waiver with a valid business type to
                          this location to generate its QR code.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <span className="text-gray-500" aria-hidden="true">
                    ⓘ
                  </span>
                  <p className="text-xs leading-5 text-gray-600">
                    The location QR code opens the location-wide signing
                    flow. Customers can select an available service and
                    waiver from there.
                  </p>
                </div>
              </section>

              <section className="overflow-hidden rounded-2xl border border-stroked bg-white">
                <div className="border-b border-stroked px-5 py-4">
                  <h2 className="font-semibold text-darkLighted">
                    Waiver-Specific QR Codes
                  </h2>
                  <p className="mt-1 text-sm text-foreground">
                    Use these when a specific activity requires its own
                    waiver.
                  </p>
                </div>

                {locationWaivers.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    No waivers are assigned to this location.
                  </div>
                ) : (
                  <div>
                    <div className="hidden grid-cols-[minmax(0,1fr)_120px_minmax(220px,auto)] gap-4 border-b border-stroked bg-gray-50 px-5 py-3 text-xs font-medium text-gray-600 md:grid">
                      <span>Waiver</span>
                      <span>Status</span>
                      <span>Actions</span>
                    </div>

                    {locationWaivers.map((waiver) => {
                      const signingUrl = getSigningUrl(
                        selectedLocation,
                        waiver
                      );
                      const qrSigningUrl = getQrSigningUrl(signingUrl);

                      const waiverName =
                        waiver.waiverName ||
                        waiver.name ||
                        getService(waiver)?.name ||
                        "Waiver";

                      const status = getWaiverStatus(waiver);
                      const qrId = `waiver-qr-${waiver._id}`;

                      return (
                        <div
                          key={waiver._id}
                          className="grid grid-cols-1 gap-3 border-b border-stroked px-5 py-4 last:border-b-0 md:grid-cols-[minmax(0,1fr)_120px_minmax(220px,auto)] md:items-center md:gap-4"
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-darkLighted">
                              {waiverName}
                            </p>
                            {getService(waiver)?.name && (
                              <p className="mt-1 text-xs text-gray-500">
                                {getService(waiver).name}
                              </p>
                            )}
                            <div className="mt-3 md:hidden">
                              <span
                                className={`inline-flex rounded-md px-2 py-1 text-xs ${
                                  status === "Active"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {status}
                              </span>
                            </div>
                          </div>

                          <div className="hidden md:block">
                            <span
                              className={`inline-flex rounded-md px-2 py-1 text-xs ${
                                status === "Active"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {status}
                            </span>
                          </div>

                          <div>
                            {qrSigningUrl ? (
                              <QRCodeActions
                                  id={qrId}
                                  url={qrSigningUrl}
                                  copyUrl={signingUrl}
                                  label={waiverName}
                              />
                            ) : (
                              <span className="text-xs text-gray-500">
                                Signing URL unavailable
                              </span>
                            )}

                            {qrSigningUrl && (
                              <div className="absolute -left-[10000px] top-0">
                                <QRCodeSVG
                                  id={qrId}
                                  value={qrSigningUrl}
                                  size={256}
                                  includeMargin
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default QRCodes;
